/**
 * Radar Regulatorio — resumen mensual de eventos del entorno regulatorio farmacéutico.
 * Orientado a perfiles de acceso al mercado: precios, financiación, autorizaciones y BOE.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useT } from "../i18n";
import { useFmt, fmtDate } from "../i18n/fmt";
import type { Lang } from "../i18n/langStore";
import { pageInfo } from "../lib/pagination";
import { groupColor } from "../lib/hitos";
import { HitoBadge, ImpactFlags, MonitoringNote } from "../components/HitoBadge";
import SearchInput from "../components/SearchInput";
import { type RadarFilters, isSearching, radarMonthParams, radarEventParams, radarExportParams } from "../lib/radarParams";

// ── Tipos ─────────────────────────────────────────────────────────────────────

type RadarItem = {
  id: string;
  cn_code: string | null;
  product_name: string | null;
  active_ingredient: string | null;
  laboratory: string | null;
  new_value: string | null;
  old_value: string | null;
  change_type: string;
  detected_at: string | null;
  relevance_score: number | null;
  is_portfolio: boolean;
  is_competitor: boolean;
  tag: "portfolio" | "competidor" | null;
  source_code: string;
  // Hito (§89): qué ha pasado, en el vocabulario del catálogo
  hito_id: string | null; hito_name: string | null;
  hito_group: number | null; hito_group_name: string | null;
  severity: "A" | "D" | "I" | null;
  sentido?: "favorable" | "adverso" | "neutro" | null;
  first_competitor?: boolean; above_precio_menor?: boolean | null;
  url: string;
  extra_data: Record<string, unknown>;
  detalle?: string | null;          // lo que cambia (el mismo texto del aviso)
  confirmed_by?: string[];          // fuentes que confirman el mismo hecho (C-14)
};

// Una «tarjeta» del Radar: carril por fuente o grupo de hitos (misma forma).
type LaneData = {
  label?: string;
  description?: string;
  color?: string;
  icon?: string;
  items: RadarItem[];
  total: number;
  portfolio_count: number;
  competitor_count: number;
  nomenclator_count: number;
  sources: { code: string; name: string; count: number }[];
};

// Hitos de un bloque, en el orden de la estructura de Carlos (feedback 20 oct 2026).
type HitoBlock = { id: string | null; nombre: string | null; total: number; portfolio_count: number;
                   competitor_count: number; items: RadarItem[] };
// Bloque del catálogo (1-11) o fuente: los mismos eventos del mes, plegados con su recuento.
type GroupData = LaneData & { grupo: number; nombre: string; hitos: HitoBlock[] };
type SourceBlock = { code: string; nombre: string; monitoring_since: string | null; total: number;
                     portfolio_count: number; competitor_count: number; hitos: HitoBlock[]; items: RadarItem[] };

type RadarResponse = {
  month: string;
  month_label: string;
  available_months: string[];
  kpis: Record<string, number>;
  lanes: Record<string, LaneData>;
  groups: GroupData[];
  by_source?: SourceBlock[];
  scope?: "mercado" | "universo";
  hito_group: number | null;
  total_events: number;
  portfolio_events?: number;
  competitor_events?: number;
  monitoring_since?: string | null;
};

type RadarSource = { code: string; name: string; lane: string | null };

// Lista plana y paginada de eventos (§76): el resumen mensual corta a 50 por carril.
type RadarEvents = {
  total: number;
  page: number;
  limit: number;
  items: RadarItem[];
  sources: RadarSource[];
};

// ── Helpers ───────────────────────────────────────────────────────────────────


function formatDate(iso: string | null, lang: Lang): string {
  if (!iso) return "—";
  return fmtDate(iso, lang, { day: "numeric", month: "short", year: "numeric" });
}

// ── Sub-components ────────────────────────────────────────────────────────────
// v3 liquid glass: el color solo vive como punto semántico pequeño; los colores
// de lane (semántica de producto) se conservan únicamente como punto/acento.

function Chevron({ open, size = 13 }: { open: boolean; size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function Dot({ color, size = 6 }: { color: string; size?: number }) {
  return <span style={{ width: size, height: size, borderRadius: size / 2, background: color, display: "inline-block", flexShrink: 0 }} />;
}

function TagBadge({ tag }: { tag: "portfolio" | "competidor" | null }) {
  const t = useT();
  if (!tag) return null;
  const c = tag === "portfolio" ? "#06b6d4" : "#f59e0b";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: c + "1e", color: c,
      borderRadius: 999, padding: "2px 9px",
      fontSize: "0.68rem", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 3, background: c, flexShrink: 0 }} />
      {tag === "portfolio" ? t("radar.portfolio") : t("radar.competitor")}
    </span>
  );
}

function SourcePill({ code, name }: { code: string; name: string }) {
  const SOURCE_ABBR: Record<string, string> = {
    sprah_bajadas: "SPRAH↓", sprah_nuevas_financiaciones: "SPRAH+",
    cipm: "CIPM", ema: "EMA", codem_cmh: "CMH",
    ipt: "IPT", boe: "BOE", nomenclator_facturacion: "Nomenclátor",
  };
  return (
    <span style={{ color: "var(--clr-text4)", fontSize: "0.66rem", fontWeight: 500, whiteSpace: "nowrap" }}>
      {SOURCE_ABBR[code] ?? name}
    </span>
  );
}

// Categoría = cápsula tintada suave SIN punto (el punto es de estados)
function CategoryLabel({ color, text }: { color: string; text: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: color + "1e", color,
      borderRadius: 999, padding: "2px 9px",
      fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {text}
    </span>
  );
}

function ItemRow({ item, laneColor }: { item: RadarItem; laneColor: string }) {
  const t = useT();
  const fmt = useFmt();
  const [expanded, setExpanded] = useState(false);

  const sc = item.source_code;
  const extra = item.extra_data || {};
  const name = item.product_name || item.new_value || "—";

  // ── Metadatos inline según fuente ────────────────────────────────────────────
  // EMA
  const atc = sc === "ema" ? (extra.atc as string | null) : null;
  const emaStatus = sc === "ema" ? (extra.status as string | null) : null;

  // SPRAH (bajadas y nuevas financiaciones)
  const sprahTipo = (sc === "sprah_bajadas" || sc === "sprah_nuevas_financiaciones")
    ? (extra.tipo as string | null) : null;
  const pvp = (sc === "sprah_bajadas" || sc === "sprah_nuevas_financiaciones")
    ? (extra.pvp as string | null) : null;
  const pvl = (sc === "sprah_bajadas" || sc === "sprah_nuevas_financiaciones")
    ? (extra.pvl as string | null) : null;

  // CMH — la sección describe el tipo de resolución (ya viene normalizada/reclasificada)
  const cmhSeccion = sc === "codem_cmh"
    ? ((extra.seccion as string | null) || (item.new_value || null)) : null;
  const cmhSeccionText = cmhSeccion && cmhSeccion !== name ? cmhSeccion : null;

  // IPT — motivo (sección) + indicación breve
  const iptSeccion = sc === "ipt" ? (extra.seccion as string | null) || (item.new_value && item.new_value !== "ipt_report" ? item.new_value : null) : null;
  const iptIndicacion = sc === "ipt" ? (extra.indicacion as string | null) : null;
  const iptLab = sc === "ipt" ? (item.laboratory || null) : null;

  // BOE — metadatos de clasificación
  const boeCategoria = sc === "boe" ? (extra.categoria_label as string | null) || (item.new_value && item.new_value !== name ? item.new_value : null) : null;
  const boeDescripcion = sc === "boe" ? (extra.descripcion as string | null) : null;
  const boeTipoDoc = sc === "boe" ? (extra.tipo_doc as string | null) : null;
  const boeOrganismo = sc === "boe" ? (extra.organismo as string | null) : null;
  const boeEmpresa = sc === "boe" ? (extra.empresa as string | null) || item.laboratory : null;

  // ── Línea de subtítulo según fuente ─────────────────────────────────────────
  let subtitle: React.ReactNode = null;

  if (sc === "ipt" && (iptSeccion || iptIndicacion || iptLab)) {
    // IPT: motivo (punto + etiqueta) + laboratorio + indicación en segunda línea
    subtitle = (
      <div style={{ marginTop: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {iptSeccion && <CategoryLabel color={laneColor} text={iptSeccion} />}
          {iptLab && (
            <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem" }}>
              · {iptLab}
            </span>
          )}
        </div>
        {iptIndicacion && (
          <div style={{
            marginTop: 3, fontSize: "0.7rem", color: "var(--clr-text4)",
            lineHeight: 1.45, fontStyle: "italic",
          }}>
            {iptIndicacion.length > 140 ? iptIndicacion.slice(0, 140) + "…" : iptIndicacion}
          </div>
        )}
      </div>
    );
  } else if (atc || (sc === "ema" && (item.active_ingredient || item.laboratory))) {
    // EMA: ATC + principio activo + laboratorio
    subtitle = (
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
        {atc && (
          <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--clr-text3)", letterSpacing: "0.3px" }}>{atc}</span>
        )}
        {item.active_ingredient && (
          <span style={{ color: "var(--clr-text3)", fontSize: "0.73rem", textTransform: "uppercase", letterSpacing: "0.3px" }}>
            {item.active_ingredient}
          </span>
        )}
        {item.laboratory && (
          <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem" }}>
            · {item.laboratory}
          </span>
        )}
        {emaStatus && emaStatus !== "Authorised" && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "#f59e0b1e", color: "#f59e0b",
            borderRadius: 999, padding: "2px 9px",
            fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap",
          }}>
            <Dot color="#f59e0b" size={5} />
            {emaStatus}
          </span>
        )}
      </div>
    );
  } else if (sc === "sprah_bajadas" || sc === "sprah_nuevas_financiaciones") {
    // SPRAH: tipo de movimiento + precio nuevo + laboratorio
    const sprahLab = item.laboratory || (extra.lab as string | null) || null;
    subtitle = (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 3 }}>
        {sprahTipo && <CategoryLabel color={laneColor} text={sprahTipo} />}
        {pvp && (
          <span style={{ color: "var(--clr-text)", fontWeight: 600, fontSize: "0.78rem" }}>
            {pvp} € PVP
          </span>
        )}
        {pvl && pvl !== pvp && (
          <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem" }}>
            PVL {pvl} €
          </span>
        )}
        {item.old_value && (
          <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem" }}>
            {t("radar.previous")} {item.old_value} €
          </span>
        )}
        {sprahLab && (
          <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem" }}>
            · {sprahLab}
          </span>
        )}
      </div>
    );
  } else if (sc === "codem_cmh" && (cmhSeccionText || item.laboratory || item.active_ingredient)) {
    // CMH: sección de la reunión + laboratorio + principio activo
    subtitle = (
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
        {cmhSeccionText && <CategoryLabel color={laneColor} text={cmhSeccionText} />}
        {item.laboratory && (
          <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem" }}>
            · {item.laboratory}
          </span>
        )}
        {item.active_ingredient && (
          <span style={{ color: "var(--clr-text4)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.2px" }}>
            · {item.active_ingredient}
          </span>
        )}
      </div>
    );
  } else if (sc === "boe" && (boeCategoria || boeDescripcion)) {
    // BOE: categoría + organismo + descripción de impacto
    subtitle = (
      <div style={{ marginTop: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {boeCategoria && <CategoryLabel color={laneColor} text={boeCategoria} />}
          {boeTipoDoc && (
            <span style={{ color: "var(--clr-text4)", fontSize: "0.68rem" }}>
              {boeTipoDoc}
            </span>
          )}
          {boeOrganismo && (
            <span style={{ color: "var(--clr-text4)", fontSize: "0.68rem" }}>
              · {boeOrganismo.replace("Agencia Española de Medicamentos y Productos Sanitarios", "AEMPS")}
            </span>
          )}
          {boeEmpresa && (
            <span style={{ color: "var(--clr-text3)", fontSize: "0.68rem", fontWeight: 500 }}>
              · {boeEmpresa}
            </span>
          )}
        </div>
        {boeDescripcion && (
          <div style={{
            marginTop: 3, fontSize: "0.7rem", color: "var(--clr-text4)",
            lineHeight: 1.45, fontStyle: "italic",
          }}>
            {boeDescripcion}
          </div>
        )}
      </div>
    );
  } else if ((item.detalle || item.new_value) && (item.detalle || item.new_value) !== name
             && (item.detalle || item.new_value) !== item.hito_name) {
    // Fallback genérico: el detalle del evento (app/eventos.py), si no el valor nuevo
    subtitle = (
      <div style={{ marginTop: 2, fontSize: "0.73rem", color: "var(--clr-text3)", fontWeight: 500 }}>
        {item.detalle || item.new_value}
      </div>
    );
  }

  // Detalle en expand (solo lo que NO se muestra ya en el subtítulo)
  const isCmh = sc === "codem_cmh";
  const hasExpandDetail = !!(
    item.cn_code ||
    (item.active_ingredient && !atc && !isCmh) ||
    (item.laboratory && !atc && !isCmh)
  );

  return (
    <div style={{
      borderBottom: "1px solid var(--clr-border2)",
      transition: "background 0.12s",
    }}>
      <div
        onClick={() => hasExpandDetail && setExpanded(e => !e)}
        style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          padding: "10px 14px", cursor: hasExpandDetail ? "pointer" : "default",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "var(--clr-hover)"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ""; }}
      >
        {/* Dot de relevancia (semántico: portfolio / competidor) */}
        <div style={{
          width: 7, height: 7, borderRadius: "50%", flexShrink: 0, marginTop: 6,
          background: item.is_portfolio ? "#06b6d4" : item.is_competitor ? "#f59e0b" : "var(--clr-border)",
        }} />

        {/* Contenido principal */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            <span style={{
              fontWeight: item.is_portfolio ? 600 : 500,
              fontSize: "0.82rem",
              color: "var(--clr-text)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              maxWidth: 340,
            }}>
              {name}
            </span>
            <TagBadge tag={item.tag} />
            <HitoBadge c={item} t={t} maxWidth={240} />
            <SourcePill code={sc} name={sc} />
            {(item.confirmed_by?.length ?? 0) > 1 && (
              <span title={item.confirmed_by!.join(", ")} style={{ fontSize: "0.62rem", fontWeight: 700, color: "var(--clr-text3)", border: "1px solid var(--clr-border)", borderRadius: 999, padding: "1px 6px" }}>
                ×{item.confirmed_by!.length}
              </span>
            )}
          </div>
          <ImpactFlags c={item} t={t} />
          {subtitle}
        </div>

        {/* Derecha: enlace directo + fecha + expand */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 4 }}>
          {item.url && (
            <a
              href={item.url} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                color: "var(--clr-text3)", fontSize: "0.72rem", fontWeight: 500,
                textDecoration: "none", whiteSpace: "nowrap",
                padding: "3px 10px", borderRadius: 999,
                background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
                lineHeight: "1.6",
              }}
            >
              {t("radar.view")}
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </a>
          )}
          <div style={{ fontSize: "0.67rem", color: "var(--clr-text4)", whiteSpace: "nowrap" }}>
            {formatDate(item.detected_at, fmt.lang)}
          </div>
          {hasExpandDetail && (
            <span style={{ color: "var(--clr-text4)", display: "inline-flex" }}>
              <Chevron open={expanded} />
            </span>
          )}
        </div>
      </div>

      {/* Detalle expandido (CN + PA + Lab si no aparecen ya en el subtítulo) */}
      {expanded && hasExpandDetail && (
        <div style={{
          padding: "6px 14px 12px 31px",
          fontSize: "0.75rem", color: "var(--clr-text3)",
          display: "flex", flexWrap: "wrap", gap: "4px 20px",
          borderTop: "1px solid var(--clr-border2)",
        }}>
          {item.cn_code && (
            <span><span style={{ color: "var(--clr-text4)" }}>CN </span>{item.cn_code}</span>
          )}
          {item.active_ingredient && !atc && (
            <span>
              <span style={{ color: "var(--clr-text4)" }}>{t("radar.paLabel")} </span>
              <span style={{ textTransform: "uppercase" }}>{item.active_ingredient}</span>
            </span>
          )}
          {item.laboratory && !atc && (
            <span><span style={{ color: "var(--clr-text4)" }}>{t("radar.labLabel")} </span>{item.laboratory}</span>
          )}
        </div>
      )}
    </div>
  );
}

// Bloque plegado (feedback de Carlos, 20 oct 2026): cabecera con el recuento; al
// desplegarlo, sus hitos en el orden de la estructura del catálogo con sus eventos.
function Block({ title, desc, color, total, portfolio, competitor, hitos, chips, open, onToggle }: {
  title: string; desc?: string | null; color: string; total: number; portfolio: number; competitor: number;
  hitos: HitoBlock[]; chips?: { code: string; name: string; count: number }[]; open: boolean; onToggle: () => void;
}) {
  const t = useT();
  const fmt = useFmt();
  const empty = total === 0;
  return (
    <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, overflow: "hidden",
                  backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)",
                  opacity: empty ? 0.6 : 1 }}>
      <div role="button" aria-expanded={open} onClick={() => !empty && onToggle()}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: empty ? "default" : "pointer",
                 borderBottom: open ? "1px solid var(--clr-border2)" : "none" }}
        onMouseEnter={e => { if (!empty) (e.currentTarget as HTMLElement).style.background = "var(--clr-hover)"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ""; }}>
        <Dot color={color} size={10} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{title}</span>
            <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{fmt.num(total)}</span>
            {portfolio > 0 && <span style={{ background: "#06b6d41e", color: "#06b6d4", borderRadius: 999, padding: "2px 9px", fontSize: "0.7rem", fontWeight: 600 }}>{t("radar.portfolioCount", { n: portfolio })}</span>}
            {competitor > 0 && <span style={{ background: "#f59e0b1e", color: "#f59e0b", borderRadius: 999, padding: "2px 9px", fontSize: "0.7rem", fontWeight: 600 }}>{t("radar.competitorCount", { n: competitor })}</span>}
          </div>
          <div style={{ fontSize: "0.71rem", color: "var(--clr-text4)", marginTop: 2 }}>
            {empty ? t("radar.blockEmpty") : desc}
          </div>
        </div>
        {chips && chips.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 1, maxWidth: "45%" }}>
            {chips.map(c => (
              <span key={c.code} style={{ border: "1px solid var(--clr-border2)", color: "var(--clr-text4)", padding: "2px 9px", borderRadius: 999, fontSize: "0.65rem" }}>
                {c.name.split("–")[0].trim()} ({c.count})
              </span>
            ))}
          </div>
        )}
        {!empty && <span style={{ color: "var(--clr-text4)", display: "inline-flex" }}><Chevron open={open} size={15} /></span>}
      </div>
      {open && hitos.map(h => <HitoSection key={h.id ?? "none"} h={h} color={color} />)}
    </div>
  );
}

function HitoSection({ h, color }: { h: HitoBlock; color: string }) {
  const t = useT();
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? h.items : h.items.slice(0, 5);
  return (
    <div style={{ borderTop: "1px solid var(--clr-border2)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "9px 16px 4px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--clr-text2)" }}>{h.nombre || t("hitos.noHito")}</span>
        <span style={{ fontSize: "0.75rem", fontWeight: 700, color }}>{h.total}</span>
        {h.portfolio_count > 0 && <span style={{ fontSize: "0.66rem", color: "#06b6d4", fontWeight: 600 }}>{t("radar.portfolioCount", { n: h.portfolio_count })}</span>}
        {h.competitor_count > 0 && <span style={{ fontSize: "0.66rem", color: "#f59e0b", fontWeight: 600 }}>{t("radar.competitorCount", { n: h.competitor_count })}</span>}
      </div>
      {visible.map(it => <ItemRow key={it.id} item={it} laneColor={color} />)}
      {(!showAll && h.items.length > 5) && (
        <div style={{ padding: "6px 16px 10px" }}>
          <button onClick={() => setShowAll(true)} style={{ background: "transparent", border: "1px solid var(--clr-border)", color: "var(--clr-text3)", borderRadius: 999, padding: "0.3rem 0.9rem", fontSize: "0.74rem", cursor: "pointer" }}>
            {t("radar.showMore", { n: h.items.length - 5 })}
          </button>
        </div>
      )}
      {(showAll || h.items.length <= 5) && h.total > h.items.length && (
        <div style={{ padding: "4px 16px 10px", fontSize: "0.72rem", color: "var(--clr-text4)" }}>{t("radar.moreInHito", { n: h.total - h.items.length })}</div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function RadarPage() {
  const t = useT();
  const fmt = useFmt();
  const { isMobile } = useBreakpoint();
  const [data, setData] = useState<RadarResponse | null>(null);
  // Búsqueda de eventos (§76). El resumen mensual corta a 50 ítems por carril;
  // esto da acceso real a los ~10.500 eventos del Radar: buscador, filtro por
  // fuente, paginación y exportación. Al buscar, los carriles dan paso a la tabla.
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  // Grupo de hitos (R-30, §90): filtra el mes Y la búsqueda. La vista por
  // defecto agrupa por grupo de hitos; «Por fuente» conserva los carriles.
  const [hitoGroup, setHitoGroup] = useState("");
  const [groupBy, setGroupBy] = useState<"hito_group" | "source">(() => {
    try { return localStorage.getItem("radar.groupBy") === "source" ? "source" : "hito_group"; } catch { return "hito_group"; }
  });
  const setGroupByPersist = (g: "hito_group" | "source") => { setGroupBy(g); try { localStorage.setItem("radar.groupBy", g); } catch { /* sin storage */ } };
  // Todo el mercado (defecto) o solo tu universo; bloques plegados al cargar (Carlos, 20 oct 2026)
  const [scope, setScope] = useState<"mercado" | "universo">("mercado");
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const toggleKey = (k: string) => setOpenKeys(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const filters: RadarFilters = { q, source: sourceFilter, hitoGroup };
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [evPage, setEvPage] = useState(1);
  const [events, setEvents] = useState<RadarEvents | null>(null);
  const [evLoading, setEvLoading] = useState(false);
  const [sources, setSources] = useState<RadarSource[]>([]);

  // La lista de fuentes hace falta aunque no estés buscando (para el selector).
  useEffect(() => {
    api.get<RadarEvents>("/radar/events?limit=1").then(r => setSources(r.sources)).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>("");

  const load = useCallback(async (m?: string, group = "", sc: "mercado" | "universo" = "mercado") => {
    setLoading(true);
    setLoadError(false);
    setOpenKeys(new Set());
    try {
      const p = radarMonthParams({ q: "", source: "", hitoGroup: group }, m);
      if (sc !== "mercado") p.set("scope", sc);
      const qs = p.toString();
      const res = await api.get<RadarResponse>(qs ? `/radar?${qs}` : "/radar");
      setData(res);
    } catch (e) {
      console.error("Radar error:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(month, hitoGroup, scope); }, [load, month, hitoGroup, scope]);

  // Sincronizar año seleccionado con el mes activo
  useEffect(() => {
    if (data?.month) setSelectedYear(data.month.slice(0, 4));
  }, [data?.month]);

  const searching = isSearching(filters);

  useEffect(() => { setEvPage(1); }, [q, sourceFilter, hitoGroup]);

  useEffect(() => {
    if (!searching) { setEvents(null); return; }
    // Buscamos en TODO el histórico, no en el mes seleccionado: el sentido de
    // esto es encontrar aquel evento concreto, no repaginar el mes.
    const params = radarEventParams(filters, evPage);
    setEvLoading(true);
    const h = setTimeout(() => {
      api.get<RadarEvents>(`/radar/events?${params}`)
        .then(setEvents)
        .catch(() => setEvents(null))
        .finally(() => setEvLoading(false));
    }, 300);
    return () => clearTimeout(h);
  }, [q, sourceFilter, hitoGroup, evPage, searching]);  // filters se deriva de estos tres

  const [exportError, setExportError] = useState("");
  const exportRadar = async (format: "xlsx" | "csv" = "xlsx") => {
    // Sin búsqueda: se exporta el mes que estás viendo (con su grupo, si lo hay).
    // Con `api.download`, que manda el token: el `a.href` de antes navegaba sin la
    // cabecera Authorization, la API respondía 401 y no se descargaba nada (el
    // mismo fallo que se arregló en Cambios en §77; aquí seguía, 28 sep 2026).
    const p = radarExportParams(filters, data?.month);
    try {
      await api.download(`/export/radar?${p}${p.toString() ? "&" : ""}format=${format}`, `helixscan_radar.${format}`);
    } catch (e: any) {
      setExportError(e?.message ?? "No se pudo exportar");
      setTimeout(() => setExportError(""), 4000);
    }
  };

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", maxWidth: 1100, margin: "0 auto", fontVariantNumeric: "tabular-nums" }}>

      {/* ── Cabecera ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
              {t("radar.title")}
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
              {t("radar.subtitle")}
            </p>
            <div style={{ marginTop: 6 }}>
              <MonitoringNote since={data?.monitoring_since} t={t} fmtDate={d => fmt.date(d)} />
            </div>
          </div>

          {/* Navegador de año + mes */}
          {data && (() => {
            const MONTH_SHORT = t("radar.monthsShort").split(",");
            const monthsByYear: Record<string, string[]> = {};
            for (const m of data.available_months) {
              const y = m.slice(0, 4);
              if (!monthsByYear[y]) monthsByYear[y] = [];
              monthsByYear[y].push(m);
            }
            const years = Object.keys(monthsByYear).sort((a, b) => b.localeCompare(a));
            const monthsInYear = monthsByYear[selectedYear] || [];

            return (
              <div style={{
                background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
                borderRadius: 18, padding: "10px 14px", minWidth: 260,
                backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
                boxShadow: "var(--clr-shadow-sm)",
              }}>
                {/* Etiqueta del mes activo */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: "0.88rem", letterSpacing: "-0.01em" }}>{data.month_label}</span>
                  <span style={{ fontSize: "0.64rem", color: "var(--clr-text4)" }}>
                    {t("radar.eventsCount", { n: fmt.num(data.total_events) })}
                  </span>
                </div>

                {/* Pills de año */}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                  {years.map(y => (
                    <button key={y} onClick={() => setSelectedYear(y)} style={{
                      padding: "2px 10px", borderRadius: 999, cursor: "pointer",
                      fontSize: "0.72rem", fontWeight: y === selectedYear ? 600 : 500,
                      background: y === selectedYear ? "var(--clr-org-primary)" : "var(--clr-surface)",
                      color: y === selectedYear ? "#fff" : "var(--clr-text3)",
                      border: `1px solid ${y === selectedYear ? "transparent" : "var(--clr-border)"}`,
                      transition: "all 0.1s",
                    }}>
                      {y}
                    </button>
                  ))}
                </div>

                {/* Grid de meses (2 filas × 6 cols) */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 3 }}>
                  {Array.from({ length: 12 }, (_, i) => {
                    const monthStr = `${selectedYear}-${String(i + 1).padStart(2, "0")}`;
                    const hasData = monthsInYear.includes(monthStr);
                    const isActive = monthStr === data.month;
                    return (
                      <button
                        key={monthStr}
                        onClick={() => hasData && setMonth(monthStr)}
                        disabled={!hasData}
                        style={{
                          padding: "4px 2px", borderRadius: 999, cursor: hasData ? "pointer" : "default",
                          fontSize: "0.67rem", fontWeight: isActive ? 600 : 400, textAlign: "center",
                          background: isActive ? "var(--clr-org-primary)" : "transparent",
                          color: isActive ? "#fff" : hasData ? "var(--clr-text)" : "var(--clr-text4)",
                          border: `1px solid ${isActive ? "transparent" : hasData ? "var(--clr-border)" : "transparent"}`,
                          opacity: hasData ? 1 : 0.25,
                          transition: "background 0.1s",
                        }}
                      >
                        {MONTH_SHORT[i]}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {loading && !data && (
        <div style={{ padding: 48, textAlign: "center", color: "var(--clr-text4)" }}>
          {t("radar.loading")}
        </div>
      )}

      {loadError && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
          borderRadius: 18, padding: "12px 16px", marginBottom: 16, flexWrap: "wrap",
          backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
          boxShadow: "var(--clr-shadow-sm)",
        }}>
          <Dot color="#ef4444" size={7} />
          <span style={{ flex: 1, fontSize: "0.85rem", color: "var(--clr-text2)", minWidth: 180 }}>{t("radar.loadErrorMsg")}</span>
          <button onClick={() => load()} style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "0.42rem 1.1rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>{t("radar.retry")}</button>
        </div>
      )}

      {data && (
        <>
          {/* ── Buscador, filtro por fuente y exportación (§76) ───────────────── */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
            <SearchInput
              value={q}
              onChange={setQ}
              placeholder={t("radar.searchPlaceholder")}
              minWidth={260}
              style={{ borderRadius: 999, padding: "7px 14px", maxWidth: 380 }} />
            <select
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              style={{
                background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)",
                borderRadius: 999, color: "var(--clr-text)", padding: "8px 16px",
                fontSize: "0.83rem", outline: "none",
              }}>
              <option value="">{t("radar.allSources")}</option>
              {sources.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
            {/* Grupo de hitos (R-30, §90): filtra el mes y la búsqueda */}
            <select
              value={hitoGroup}
              onChange={e => setHitoGroup(e.target.value)}
              style={{
                background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)",
                borderRadius: 999, color: "var(--clr-text)", padding: "8px 16px",
                fontSize: "0.83rem", outline: "none",
              }}>
              <option value="">{t("hitos.allGroups")}</option>
              {data.groups.filter(g => g.grupo > 0).map(g => (
                <option key={g.grupo} value={g.grupo}>{t(`hitos.group${g.grupo}`)}</option>
              ))}
            </select>
            {/* Vista: por grupo de hitos (defecto) o por fuente (carriles) */}
            {!searching && (
              <div style={{ display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden", background: "var(--clr-surface)" }}>
                {(["hito_group", "source"] as const).map(g => (
                  <button key={g} onClick={() => setGroupByPersist(g)} style={{
                    border: 0, cursor: "pointer", padding: "8px 14px", fontSize: "0.78rem", fontWeight: 600,
                    background: groupBy === g ? "var(--clr-org-primary)" : "transparent",
                    color: groupBy === g ? "#fff" : "var(--clr-text3)",
                  }}>
                    {g === "hito_group" ? t("radar.viewByBlock") : t("radar.viewBySource")}
                  </button>
                ))}
              </div>
            )}
            {!searching && (
              <div title={t("radar.scopeHint")} style={{ display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden", background: "var(--clr-surface)" }}>
                {(["mercado", "universo"] as const).map(sc => (
                  <button key={sc} onClick={() => setScope(sc)} style={{
                    border: 0, cursor: "pointer", padding: "8px 14px", fontSize: "0.78rem", fontWeight: 600,
                    background: scope === sc ? "var(--clr-org-primary)" : "transparent",
                    color: scope === sc ? "#fff" : "var(--clr-text3)",
                  }}>
                    {sc === "mercado" ? t("radar.scopeMarket") : t("radar.scopeUniverse")}
                  </button>
                ))}
              </div>
            )}
            {exportError && <span style={{ color: "#f87171", fontSize: "0.8rem" }}>{exportError}</span>}
            <button
              onClick={() => exportRadar("xlsx")}
              style={{
                background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999,
                padding: "8px 18px", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
              }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
              {t("radar.exportExcel")}
            </button>
            {/* Contrato con Italfarmaco, punto 13: también en CSV */}
            <button onClick={() => exportRadar("csv")}
              style={{ background: "transparent", color: "var(--clr-org-primary)", border: "1px solid var(--clr-org-primary)",
                       borderRadius: 999, padding: "7px 14px", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}>
              {t("export.csv")}
            </button>
          </div>

          {!searching && (() => {
            // ── Por bloque del catálogo (los 11, en su orden) o por fuente ─────
            const blocks = data.groups.filter(g => g.grupo > 0 || g.total > 0);
            const gTitle = (g: GroupData) => g.grupo === 0 ? t("hitos.noHito") : t(`hitos.group${g.grupo}`);
            const hitosDesc = (hs: HitoBlock[]) => hs.length === 1 ? (hs[0].nombre ?? "") : t("radar.hitosInBlock", { n: hs.length });
            const keys = groupBy === "hito_group" ? blocks.filter(g => g.total > 0).map(g => `g${g.grupo}`)
              : (data.by_source ?? []).map(b => `s${b.code}`);
            const allOpen = keys.length > 0 && keys.every(k => openKeys.has(k));
            return (
              <>
                {/* Franja: los 11 bloques con su recuento, en el orden del catálogo */}
                <div style={{
                  background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
                  borderRadius: 18, padding: "1rem 1.25rem", marginBottom: 18,
                  backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
                  boxShadow: "var(--clr-shadow-sm)", display: "grid",
                  gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(130px, 1fr))", gap: "14px 16px",
                }}>
                  {blocks.map(g => (
                    <button key={g.grupo} disabled={g.total === 0}
                      onClick={() => { setGroupByPersist("hito_group"); setOpenKeys(prev => new Set(prev).add(`g${g.grupo}`)); }}
                      style={{ textAlign: "left", background: "none", border: 0, padding: 0, cursor: g.total ? "pointer" : "default", opacity: g.total ? 1 : 0.45, fontFamily: "inherit" }}>
                      <div style={{ fontSize: "1.4rem", fontWeight: 700, color: groupColor(g.grupo || null), lineHeight: 1.05 }}>{fmt.num(g.total)}</div>
                      <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.7 }}>{gTitle(g)}</div>
                      {(g.portfolio_count > 0 || g.competitor_count > 0) && (
                        <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", marginTop: 2 }}>
                          {g.portfolio_count > 0 && t("radar.kpiPortfolio", { n: g.portfolio_count })}
                          {g.portfolio_count > 0 && g.competitor_count > 0 && " · "}
                          {g.competitor_count > 0 && t("radar.kpiComp", { n: g.competitor_count })}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                {keys.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <button onClick={() => setOpenKeys(allOpen ? new Set() : new Set(keys))}
                      style={{ background: "transparent", border: 0, color: "var(--clr-org-primary)", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
                      {allOpen ? t("radar.collapseAll") : t("radar.expandAll")}
                    </button>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {groupBy === "hito_group" && blocks.map(g => (
                    <Block key={`g${g.grupo}`} title={gTitle(g)} desc={hitosDesc(g.hitos)} color={groupColor(g.grupo || null)}
                      total={g.total} portfolio={g.portfolio_count} competitor={g.competitor_count} hitos={g.hitos}
                      chips={g.sources} open={openKeys.has(`g${g.grupo}`)} onToggle={() => toggleKey(`g${g.grupo}`)} />
                  ))}
                  {groupBy === "source" && (data.by_source ?? []).length === 0 && (
                    <div style={{ padding: "24px 16px", color: "var(--clr-text4)", fontSize: "0.82rem", textAlign: "center" }}>
                      {hitoGroup ? t("radar.noGroupEvents") : t("radar.noEvents")}
                    </div>
                  )}
                  {groupBy === "source" && (data.by_source ?? []).map(b => (
                    <Block key={`s${b.code}`} title={b.nombre}
                      desc={[b.monitoring_since ? t("radar.sourceSince", { date: fmt.date(new Date(b.monitoring_since)) }) : null, hitosDesc(b.hitos)].filter(Boolean).join(" · ")}
                      color="#64748b" total={b.total} portfolio={b.portfolio_count} competitor={b.competitor_count}
                      hitos={b.hitos} open={openKeys.has(`s${b.code}`)} onToggle={() => toggleKey(`s${b.code}`)} />
                  ))}
                </div>
              </>
            );
          })()}

          {/* ── Resultados de búsqueda: sustituyen a los carriles (§76) ───────── */}
          {searching && (
            <div style={{
              background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
              borderRadius: 18, overflow: "hidden",
              backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
              boxShadow: "var(--clr-shadow-sm)",
            }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--clr-border2)", fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                {evLoading
                  ? t("radar.searching")
                  : t("radar.resultsCount", { n: (events?.total ?? 0).toLocaleString("es-ES") })}
              </div>

              {!evLoading && events && events.items.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "1px solid var(--clr-border2)" }}>
                        {[t("radar.colDate"), t("radar.colHito"), t("radar.colSource"), t("radar.colProduct"),
                          t("radar.colActiveIngredient"), t("radar.colLaboratory")].map(h => (
                          <th key={h} style={{ padding: "0.7rem 12px", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {events.items.map(it => {
                        const src = sources.find(s => s.code === it.source_code);
                        return (
                          <tr key={it.id} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                            <td style={{ padding: "8px 12px", color: "var(--clr-text3)", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                              {it.detected_at ? it.detected_at.slice(0, 10) : "–"}
                            </td>
                            <td style={{ padding: "8px 12px" }}><HitoBadge c={it} t={t} maxWidth={260} /><ImpactFlags c={it} t={t} /></td>
                            <td style={{ padding: "8px 12px", color: "var(--clr-text3)", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                              {src?.name ?? it.source_code}
                            </td>
                            <td style={{ padding: "8px 12px", maxWidth: 320 }}>
                              {it.url
                                ? <a href={it.url} target="_blank" rel="noreferrer" style={{ color: "var(--clr-org-primary)", fontWeight: 600, textDecoration: "none" }}>{it.product_name} ↗</a>
                                : <span style={{ fontWeight: 500 }}>{it.product_name || "–"}</span>}
                            </td>
                            <td style={{ padding: "8px 12px", color: "var(--clr-text2)", textTransform: "uppercase", fontSize: "0.75rem" }}>{it.active_ingredient || "–"}</td>
                            <td style={{ padding: "8px 12px", color: "var(--clr-text3)", fontSize: "0.78rem" }}>{it.laboratory || "–"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {!evLoading && events && events.total === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: "var(--clr-text4)" }}>{t("radar.noResults")}</div>
              )}

              {events && events.total > events.limit && (() => {
                const { totalPages, hasPrev, hasNext } = pageInfo(events.total, events.limit, events.page);
                return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid var(--clr-border2)", fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                  <span>{t("radar.pageOf", { page: events.page, total: totalPages })}</span>
                  <span style={{ display: "flex", gap: 8 }}>
                    <button disabled={!hasPrev} onClick={() => setEvPage(p => p - 1)}
                      style={{ background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 999, padding: "4px 14px", cursor: !hasPrev ? "default" : "pointer", opacity: !hasPrev ? 0.4 : 1, color: "var(--clr-text)" }}>‹</button>
                    <button disabled={!hasNext} onClick={() => setEvPage(p => p + 1)}
                      style={{ background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 999, padding: "4px 14px", cursor: !hasNext ? "default" : "pointer", opacity: !hasNext ? 0.4 : 1, color: "var(--clr-text)" }}>›</button>
                  </span>
                </div>
                );
              })()}
            </div>
          )}

          {/* ── Footer note ────────────────────────────────────────────────── */}
          <div style={{ marginTop: 24, padding: "12px 16px", borderRadius: 18,
            background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
            backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
            boxShadow: "var(--clr-shadow-sm)",
            fontSize: "0.72rem", color: "var(--clr-text4)", lineHeight: 1.6,
          }}>
            <strong style={{ color: "var(--clr-text3)" }}>{t("radar.sourcesLabel")}</strong>{" "}
            {sources.map(s2 => s2.name.split("–")[0].trim()).filter((v, i, a) => a.indexOf(v) === i).join(" · ")}.{" "}
            {t("radar.footerNote")}
          </div>
        </>
      )}
    </div>
  );
}
